import { useCallback, useEffect, useMemo, useRef } from "react";
import GridColumn from "../../../GridColumns/GridColumn";
import { GridFilterSettings } from "../../types/GridFilterSettings";
import { GridViewModule, useGridViewModule } from "./useGridViewModule";
import ApiClient from "../../../../../lib/apiClient";
import AppToasterV2 from "../../../../ToastV2/ToastV2";

export interface UseGridViewModule extends GridViewModule {
  filterSettings: GridFilterSettings | null;
  queryKey: string | null;
}

export const useGridView = (
  gridId: string,
  gridTitle: string,
  columnsConfiguration: GridColumn[],
  getColumnsConfigurationForUser: () => GridColumn[],
  apiClient: ApiClient,
  filterSettings: GridFilterSettings | null,
  gridAutoName?: string,
  defaultGridViewTitle?: string,
  shouldCreateAutoView: boolean = true,
  pathToUserViewApi?: string,
  expectSensibleDependencies: boolean = false,
  refreshViewsAfterSaving: boolean = false
): UseGridViewModule => {
  const lastLoadedGridId = useRef<string>(null);

  const module = useGridViewModule(
    apiClient,
    pathToUserViewApi,
    refreshViewsAfterSaving
  );

  const { loadViews, updateViewFilterSettings } = module;

  const queryKey = useMemo(() => {
    if (!!filterSettings?.rawQuery) {
      return JSON.stringify(filterSettings);
    }
    return null;
  }, [filterSettings?.rawQuery, filterSettings?.columnNames]);

  useEffect(() => {
    if (!queryKey) {
      return;
    }

    let parsedQuery: GridFilterSettings | null = null;

    try {
      parsedQuery = JSON.parse(queryKey) as GridFilterSettings;
    } catch (error) {
      console.warn("Error parsing query key", error);
      return;
    }

    if (parsedQuery.gridKey === gridId) {
      try {
        updateViewFilterSettings(gridId, parsedQuery);
      } catch (error) {
        console.error("Error updating view filter settings", error);
      }
    } else {
      // This is only a problem if we expect our dependencies to make sense, i.e. in reactive mode
      if (expectSensibleDependencies) {
        console.warn(
          "Query key does not match grid id",
          parsedQuery.gridKey,
          gridId
        );
      }
    }
  }, [queryKey, updateViewFilterSettings, gridId]);

  // Load View
  const loadViewsAsync = useCallback(async () => {
    await loadViews(
      gridTitle,
      gridId,
      columnsConfiguration,
      getColumnsConfigurationForUser,
      shouldCreateAutoView,
      defaultGridViewTitle,
      gridAutoName
    ).catch(() => {
      AppToasterV2.showFailedActionToaster(
        "An error occurred loading the data."
      );
    });
  }, [
    gridTitle,
    gridId,
    columnsConfiguration,
    getColumnsConfigurationForUser,
    shouldCreateAutoView,
    defaultGridViewTitle,
    gridAutoName,
    loadViews,
  ]);

  useEffect(() => {
    if (lastLoadedGridId.current === gridId) {
      return;
    }

    lastLoadedGridId.current = gridId;
    loadViewsAsync();
  }, [gridId, loadViewsAsync]);

  return {
    ...module,
    filterSettings,
    queryKey,
  };
};
